import { useBrief, useSetting } from '@/hooks';
import { log } from '@/utils';
import { Box, Drawer, Step, StepButton, Stepper, Toolbar } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { Section } from './Section';

export function Brief() {
  const { code } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const page = searchParams.get('page');
  const logged = useRef(false);
  const previousPage = useRef(null);
  const {
    query: { data: brief },
  } = useBrief(code);
  const {
    query: { data: sections },
  } = useSetting('sections');
  const [completed, setCompleted] = useState([]);
  const includedSections = useMemo(
    () =>
      brief
        ? Object.entries(sections || {}).filter(
            ([key]) => !brief.excludedSections.includes(key),
          )
        : [],
    [brief, sections],
  );

  useEffect(() => {
    if (!logged.current && brief) {
      log('View', 'Brief', {
        code,
        title: brief?.title,
        groupCodes: brief.groupCodes,
      });

      logged.current = true;
    }
  });

  useEffect(() => {
    if (
      page !== null &&
      brief !== null &&
      includedSections.length > 0 &&
      page !== previousPage.current
    ) {
      log('View', includedSections[+page][1].title, {
        code,
        groupCodes: brief?.groupCodes,
      });

      previousPage.current = page;
    }
  }, [brief, code, includedSections, page]);

  const handleStep = (page) => () => {
    setSearchParams({
      page,
    });
  };

  const handleComplete = (page) => () => {
    const incompletePages = [...Array(includedSections.length).keys()].filter(
      (p) => !completed.includes(p),
    );

    log('Done', includedSections[+page][1].title, {
      code,
      groupCodes: brief.groupCodes,
    });

    if (incompletePages.length === 1) {
      navigate('../');
    } else {
      setCompleted((existingValue) => existingValue.concat(+page));
      setSearchParams({
        page:
          incompletePages.filter((ip) => ip > +page && ip !== +page)[0] ??
          incompletePages[0] ??
          0,
      });
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <title>
        {['IR3', 'Briefs', brief?.title, includedSections?.[page]?.[1].title]
          .filter(Boolean)
          .join(' | ')}
      </title>
      <Drawer
        variant="permanent"
        sx={{
          width: 240,
          flexShrink: 0,
          ['& .MuiDrawer-paper']: {
            width: 240,
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar variant="dense" />
        <Box sx={{ overflow: 'auto', p: 1 }}>
          <Stepper nonLinear activeStep={+(page ?? 0)} orientation="vertical">
            {includedSections.map(([key, { title }], index) => (
              <Step key={key} completed={completed.includes(index)}>
                <StepButton color="inherit" onClick={handleStep(index)}>
                  {title}
                </StepButton>
              </Step>
            ))}
          </Stepper>
        </Box>
      </Drawer>
      <Box
        component="main"
        sx={{
          height: 'calc(100vh - 48px)',
          width: 'calc(100vw - 240px)',
          bgcolor: 'background.paper',
        }}
      >
        {includedSections.length > 0 && (
          <Section
            id={includedSections?.[page]?.[0] ?? ''}
            title={includedSections?.[page]?.[1].title ?? ''}
            groupCodes={brief.groupCodes}
            onComplete={handleComplete(page)}
          />
        )}
      </Box>
    </Box>
  );
}
